import {
    ColumnDatatable,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import { Formalite } from '@europrocurement/l2d-domain/reducers/formalites/slices/formalite';
import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { FaOptionIcon, formalityAddIcon } from '@europrocurement/l2d-icons';
import { CategoriesFormalite } from '@europrocurement/l2d-domain/reducers/formalites/slices/categoriesFormalite';
import {
    ContextActionType,
    LazyBreadcrumb,
    useDumbActionsContext,
} from '@europrocurement/flexy-components';

import { Box, Chip, Typography } from '@mui/material';
import {
    categoriesFormalitySelector,
    formalitiesSelector,
    formulairesFormaliteSelector,
    useFormaliteService,
    useSociete,
} from '@europrocurement/l2d-domain';
import { FormalityAppDispatch } from '@europrocurement/l2d-domain/reducers/formalites/types';
import { PossibleTypes, useSearchBar } from '@europrocurement/l2d-modules';
import { FormulaireFormalite } from '@europrocurement/l2d-domain/openApi/ApiFormulaire';
import { useClickActions } from '@europrocurement/l2d-hooks';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import FormalityListItemActions from './FormalityListItemActions';
import { FORMALITY_NEW } from '../../constants/paths';
import useCharge from '../../hooks/useCharge';

const FormalitiesListing: React.FunctionComponent = function () {
    const { setIsSearchBarPath, setSearchBarType } = useSearchBar();
    const { getFormalites } = useFormaliteService();
    const datasource = useSelector(formalitiesSelector);
    const xIdSociete = useSociete();
    const formulairesDatasource = useSelector(formulairesFormaliteSelector);
    const categoriesDatasource = useSelector(categoriesFormalitySelector);
    const navigate = useNavigate();
    const dispatch = useDispatch<FormalityAppDispatch>();
    const { navigateOnClick } = useClickActions({ navigate });
    const { getChargeLabel } = useCharge();

    // Activation du SearchBar pour les formalités
    React.useEffect(() => {
        setSearchBarType(PossibleTypes.Formality);
        setIsSearchBarPath(true);
        return () => {
            setSearchBarType(undefined);
            setIsSearchBarPath(false);
        };
    }, [setIsSearchBarPath, setSearchBarType]);

    const handleRowClick = React.useCallback(
        (__: React.MouseEvent<HTMLElement>, { id }: Formalite) => {
            navigateOnClick({ path: `${id}` });
        },
        [navigateOnClick],
    );

    const handleRowWheelClick = React.useCallback(
        (__: React.MouseEvent<HTMLElement>, { id }: Formalite) => {
            navigateOnClick({ path: `${id}`, openNewTab: true });
        },
        [navigateOnClick],
    );

    const contextActions: ContextActionType[] = [
        {
            name: 'Ajouter une formalité',
            icon: <FaOptionIcon {...formalityAddIcon.props} />,
            action: () => navigate(FORMALITY_NEW),
        },
    ];

    // Chargement initial des données
    React.useEffect(() => {
        dispatch(getFormalites({}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useDumbActionsContext(contextActions);

    const columns: ColumnDatatable<Formalite>[] = useMemo(
        () => [
            {
                label: 'Libellé',
                render: ({ libelle }: Formalite) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {libelle}
                    </Typography>
                ),
                isDisplayed: true,
                sortable: true,
                field: 'libelle',
            },
            {
                label: 'Icône',
                render: ({ iconeName }) => {
                    if (iconeName === null) return null;

                    return <FaOptionIcon icon={['fad', iconeName] as IconProp} />;
                },
                isDisplayed: false,
            },
            {
                label: 'Catégorie',
                render: (item: Formalite) => (
                    <Box sx={{ display: 'flex' }}>
                        <Typography
                            display="inline"
                            variant="l2dtableCell"
                            color="text.gris70"
                            sx={{
                                fontSize: '20px',
                            }}
                        >
                            {item.categorie?.iconeName ? (
                                <FaOptionIcon icon={['fad', item.categorie?.iconeName]} />
                            ) : null}
                        </Typography>
                        <Typography
                            variant="l2dtableCell"
                            color="text.gris70"
                            sx={{ marginLeft: '15px' }}
                        >
                            {item.categorie?.libelle ? item.categorie?.libelle : null}
                        </Typography>
                    </Box>
                ),
                isDisplayed: true,
                sortable: true,
                field: 'categorie',
            },
            {
                label: 'Formulaire',
                render: (item: Formalite) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {formulairesDatasource.data.find(
                            (formulaireFormalityItem: FormulaireFormalite) =>
                                formulaireFormalityItem.id === item.formulaire,
                        )?.libelle || 'Aucun'}
                    </Typography>
                ),
                isDisplayed: true,
                sortable: false,
                field: 'formulaire',
            },
            {
                label: 'Rang',
                render: ({ rang }: Formalite) => (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {rang}
                    </Typography>
                ),
                isDisplayed: true,
                sortable: true,
                field: 'rang',
            },
            {
                label: 'Charge',
                render: ({ charge }) => getChargeLabel(charge as number),
                isDisplayed: true,
                sortable: true,
                field: 'charge',
            },
            {
                label: 'Statut',
                displayLabel: false,
                render: ({ deleted }: Formalite) => (
                    <div>
                        {deleted ? (
                            <Chip
                                color="warning"
                                label="Inactif"
                            />
                        ) : (
                            <Chip
                                color="success"
                                label="Actif"
                            />
                        )}
                    </div>
                ),
                isDisplayed: false,
            },
            {
                label: 'Actions',
                displayLabel: false,
                render: (item) =>
                    item.id && (
                        <FormalityListItemActions
                            item={item}
                            onItemUpdate={() => dispatch(getFormalites({}))}
                        />
                    ),
                isDisplayed: true,
            },
        ],
        [dispatch, formulairesDatasource.data, getFormalites, getChargeLabel],
    );

    const filters: FiltersDatatableList = [
        {
            label: 'Libellé',
            field: 'libelle',
            type: 'text',
        },
        {
            label: 'Catégorie',
            field: 'categorie',
            type: 'staticSelect',
            options: categoriesDatasource.data.map((category: CategoriesFormalite) => ({
                value: category.id,
                label: category.libelle,
            })),
            renderFilterList: (value) =>
                categoriesDatasource.data.find(
                    (category: CategoriesFormalite) => category.id === value,
                )?.libelle,
        },
        {
            label: 'Formalités inactives ?',
            field: 'deleted',
            type: 'boolean',
        },
    ];

    return datasource ? (
        <>
            <LazyBreadcrumb
                path={[
                    { path: 'Formalités', link: '/prescripteurs/liste' },
                    "Recherche d'une formalité",
                    'Liste des formalités',
                ]}
            />
            <StoreDatatable
                dataSource={datasource}
                columns={columns}
                fetchData={getFormalites}
                filters={filters}
                localStorageKey="FlexyStoreDatatable"
                onClickRow={handleRowClick}
                onWheelClickRow={handleRowWheelClick}
                observers={[xIdSociete]}
            />
        </>
    ) : null;
};

export default FormalitiesListing;
